import { FC, useCallback, useMemo } from 'react'
import styled from 'styled-components'

import { TypeStyle, TypeStyleBodySmall, untilMd } from '@syconium/little-miss-figgy'

import { EmbroideryTypeEnum } from '../../../../__generated__/graphql/catalog/graphql'
import { useFeatureFlag } from '../../../../app/_providers/ExperimentationProvider.client'
import { useLocalization } from '../../../../app/_providers/LocalizationProvider.client'
import { useTranslation } from '../../../../app/_providers/TranslationProvider.client'
import { EmbroideryService } from '../../../../containers/EmbroideryService'
import { EmbroideryModalService } from '../../../../containers/EmbroideryService/EmbroideryModalService'
import { getFormKind } from '../../../../containers/EmbroideryService/utils'
import { intlPrettyCurrency } from '../../../../lib/utils'
import { Embroidery } from '../../../../types/graphql'
import { LogoSneakPeek } from '../../EmbroiderySneakPeek/LogoSneakPeek'
import { normalizeDigitizationSettingToEmbroideryKind } from '../../forms/DTCLogoEmbroideryForm/utils'

import { EmbroideryOption } from './EmbroideryOption'
import { PriceOptions, PriceSummary } from './PriceSummary'

export const EmbroideryOptionList: FC<{
  embroideryOptions: ReadonlyArray<Embroidery>
}> = ({ embroideryOptions }) => {
  const { formErrors, withPricing, requiredEmbroideryOptions } =
    EmbroideryModalService.useContainer()

  const {
    selectedEmbroideryFormOptions,
    setSelectedFormKind,
    defaultSelectedEmbroideryOptions,
    selectedEmbroideryOptions,
    productColorHex,
  } = EmbroideryService.useContainer()

  const { locale, region } = useLocalization()
  const {
    embroidery: {
      modal: strings,
      validation,
      embroideryOptionTitles,
      from: logoPricePrefix,
      edit: editButtonText,
      subtitle,
    },
  } = useTranslation()

  const showPriceSummary =
    useFeatureFlag<'true' | 'false'>({
      key: 'show-embroidery-price-summary',
      controlVariant: 'hidePriceSummary',
      ifAccessedPriorToDecisionInitialization: 'return-null-while-pending',
    }) === 'true'

  const dtcLogoOption = embroideryOptions.find(option => option.kind === 'LOGO_SETUP')

  const validEmbroideryOptions = useMemo(() => {
    const options = embroideryOptions.filter(option =>
      ['LOGO_ONLY', 'TEXT_ONLY', 'ICON_ONLY'].includes(option.kind)
    )
    if (dtcLogoOption) {
      options.push(dtcLogoOption)
    }
    return options
  }, [embroideryOptions, dtcLogoOption])

  const priceOptions = Object.entries(selectedEmbroideryOptions).reduce(
    (acc: PriceOptions, curr) => {
      if (curr?.length > 1 && curr[1]) {
        if (curr[1].embroideryItemVariant?.price) {
          const key = curr[0]
          acc[key] = curr[1].embroideryItemVariant.price
        }
        if (curr[1].embroiderySetupVariant?.price) {
          const key = `${[curr[0]]}_SETUP`
          acc[key] = curr[1].embroiderySetupVariant.price ?? 0
        }
      }
      return acc
    },
    {}
  )

  const selectedOptions = useMemo(() => {
    return {
      isLogoSelected:
        !!selectedEmbroideryOptions.LOGO_SETUP || !!selectedEmbroideryOptions.ADD_LOGO,
      isIconSelected: !!selectedEmbroideryOptions.ICON_ONLY,
    }
  }, [selectedEmbroideryOptions])

  const disabledText: Partial<Record<EmbroideryTypeEnum, string>> = {
    ICON_ONLY: strings.iconUnavailable,
    LOGO_SETUP: strings.logoUnavailable,
  }

  const getFormattedPrice = useCallback(
    (option: Embroidery) => {
      let logoPrice = 0

      if (option.kind === 'LOGO_SETUP') {
        // If no logo is selected yet, show the lowest default price (from +$20)
        if (!selectedEmbroideryFormOptions.logoFile) {
          logoPrice =
            embroideryOptions?.find(option => option.kind === 'LOGO_BLACK_WHITE')?.variants[0]
              ?.price ?? 0
        } else {
          // If setup file exists, include its price in the total price ($60)
          if (selectedEmbroideryFormOptions.setupFile) {
            logoPrice += option?.variants[0]?.price ?? 0
          }
          // Add price for the selected color option
          const logoColorPrice =
            embroideryOptions?.find(
              option =>
                option.kind ===
                normalizeDigitizationSettingToEmbroideryKind(
                  selectedEmbroideryFormOptions.logoColor
                )
            )?.variants[0]?.price ?? 0

          logoPrice += logoColorPrice
        }
      } else {
        // For other embroidery options, use their own price
        logoPrice = option?.variants[0]?.price ?? 0
      }

      return logoPrice > 0
        ? intlPrettyCurrency({
            unit: 'cents',
            value: logoPrice,
            region: region,
            locale: locale,
          })
        : undefined
    },
    [locale, region, selectedEmbroideryFormOptions, embroideryOptions]
  )

  const hasTextPrefix = useCallback(
    (kind: EmbroideryTypeEnum) => {
      if (
        kind === 'LOGO_SETUP' &&
        !selectedEmbroideryFormOptions.setupFile &&
        !selectedEmbroideryFormOptions.logoFile
      ) {
        return <>{logoPricePrefix}</>
      }
      return null
    },
    [
      selectedEmbroideryFormOptions.setupFile,
      logoPricePrefix,
      selectedEmbroideryFormOptions.logoFile,
    ]
  )

  return (
    <EmbroideryOption.List>
      {validEmbroideryOptions.map((option, index) => {
        // Don't render embroidery options that don't have a valid form kind
        const formKind = getFormKind(option.kind)
        if (!formKind) return null

        const price = getFormattedPrice(option)
        const disabled =
          (option.kind === 'ICON_ONLY' && selectedOptions.isLogoSelected) ||
          (option.kind === 'LOGO_SETUP' && selectedOptions.isIconSelected) ||
          Boolean(defaultSelectedEmbroideryOptions?.[formKind])

        const selected =
          formKind === 'LOGO_SETUP'
            ? !!selectedEmbroideryOptions.ADD_LOGO
            : !!selectedEmbroideryOptions[formKind]

        const subtitleText = selected ? editButtonText : subtitle

        // Render the default logo sneak peek instead of the option button if defined as a default selected option
        const displayDefaultLogoSelection =
          option.kind === 'LOGO_ONLY' && defaultSelectedEmbroideryOptions?.LOGO_ONLY

        // Indicate required embroidery options
        const required = requiredEmbroideryOptions.includes(option.kind)

        const error = formErrors[formKind]

        const key = option.kind.includes('LOGO')
          ? 'LOGO_SETUP'
          : (`${option.kind}` as keyof typeof embroideryOptionTitles)
        const title = embroideryOptionTitles[key] ?? ''
        const label = `${title}${required ? ` (${validation.required})` : ''}`

        if (displayDefaultLogoSelection)
          return (
            <StyledLogoSneakPeek
              key={label}
              data-testid='embroidery-option-logo-sneak-peek'
              backgroundColor={productColorHex}
              imgSrc={defaultSelectedEmbroideryOptions.LOGO_ONLY?.logoImageSource}
              logoName={defaultSelectedEmbroideryOptions.LOGO_ONLY?.logoName}
              logoPlacement={defaultSelectedEmbroideryOptions?.LOGO_ONLY?.logoPlacement}
            />
          )

        return (
          <EmbroideryOption.ListOption
            key={index}
            index={index}
            kind={formKind}
            handleClick={selected ? () => null : setSelectedFormKind}
            label={label}
            disabled={disabled}
            selected={selected}
            error={
              error ? (
                <EmbroideryOption.Error id={`${option.kind}_formerror`} message={error} />
              ) : null
            }
            errorMessageId={`${option.kind}_formerror`}
          >
            <LeftContent data-testid='embroidery-option-styled-selector' data-selected={selected}>
              <ListOptionTitle $disabled={disabled} $isInvalid={Boolean(error)}>
                {label}
              </ListOptionTitle>
              <ListOptionSubtitle>
                {selected ? (
                  <>
                    <ListOptionEditButton
                      aria-label={selected ? editButtonText : ''}
                      onClick={() => setSelectedFormKind(formKind)}
                    >
                      {subtitleText}
                    </ListOptionEditButton>
                    {withPricing && <span> +{price}</span>}
                  </>
                ) : (
                  !disabled && <span>{subtitle}</span>
                )}
              </ListOptionSubtitle>
            </LeftContent>
            {disabled ? (
              <DisabledMessage>{disabledText[option.kind]}</DisabledMessage>
            ) : (
              <EmbroideryOption.RightContent $disabled={disabled}>
                {withPricing && !selected && (
                  <span aria-hidden='true'>
                    {hasTextPrefix(option.kind)} +{price}
                  </span>
                )}
                <EmbroideryOption.Icon />
              </EmbroideryOption.RightContent>
            )}
          </EmbroideryOption.ListOption>
        )
      })}
      {showPriceSummary && withPricing && <PriceSummary priceOptions={priceOptions} />}
    </EmbroideryOption.List>
  )
}

const LeftContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`

const ListOptionTitle = styled.div<{ $disabled?: boolean; $isInvalid?: boolean }>`
  ${TypeStyle.css.headlineMicro}
  text-transform: uppercase;
  color: ${o =>
    o.$disabled
      ? o.theme.color.stroke.disabled.text.on.background
      : o.$isInvalid
      ? o.theme.color.text.critical.on.background
      : o.theme.color.text.primary.on.background};
  letter-spacing: ${o => o.theme.typography.font.letterSpacing.wide};
  font-weight: ${o => o.theme.typography.font.weight[700]};
  line-height: ${o => o.theme.typography.font.lineHeight.loose};
`
const ListOptionSubtitle = styled.div`
  color: ${o => o.theme.color.text.secondary.on.background};
`

const ListOptionEditButton = styled.button`
  text-decoration: underline;
  cursor: pointer;
  font-weight: ${o => o.theme.typography.font.weight[600]};
`

const StyledLogoSneakPeek = styled(LogoSneakPeek)`
  margin-top: ${o => o.theme.spacing(4)};
`

const DisabledMessage = styled(TypeStyleBodySmall)`
  font-style: italic;
  color: ${o => o.theme.color.deprecated.stoneDark};
  font-weight: ${o => o.theme.typography.font.weight[600]};
  line-height: 20px;
  letter-spacing: 0.6px;
  align-self: center;

  ${untilMd} {
    font-size: 10px;
    font-weight: ${o => o.theme.typography.font.weight[600]};
    line-height: 12px;
    letter-spacing: 0.4px;
  }
`
